import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { FaFire, FaClock, FaSearch, FaFilter, FaTimes, FaArrowRight } from "react-icons/fa";
// Components
import TopNavbar from "../components/Nav/TopNavbar";
import BlogBox from "../components/Elements/BlogBox";
import AnimateOnScroll from "../components/AnimateOnScroll";
import Footer from "../components/Sections/Footer";
// Services
import { getBlogs } from "../services/blogApi";

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffTime = Math.abs(now - date);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return '1 day ago';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return `${Math.floor(diffDays / 365)} years ago`;
};

const getAuthorName = (createdBy) => {
  return 'AFRIQ Admin';
};

const getSlugFromId = (id) => {
  return id || 'blog-post';
};

export default function AllBlogs() {
  const [allBlogs, setAllBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const blogsPerPage = 12;

  useEffect(() => {
    const fetchAllBlogs = async () => {
      try {
        setLoading(true);
        setError(null);
        // Fetch a large number of blogs to have enough for filtering
        const response = await getBlogs({ skip: 0, limit: 100 });
        if (response && response.data && Array.isArray(response.data)) {
          setAllBlogs(response.data);
        } else {
          setError('Unexpected response format from API');
        }
      } catch (err) {
        console.error('Error fetching blogs:', err);
        setError(err.message || 'Failed to load blog posts');
      } finally {
        setLoading(false);
      }
    };

    fetchAllBlogs();
  }, []);

  // Get unique categories
  const categories = useMemo(() => {
    const cats = ['all', ...new Set(allBlogs.map(blog => blog.category || 'Uncategorized').filter(Boolean))];
    return cats;
  }, [allBlogs]);

  // Filter and search blogs
  const filteredBlogs = useMemo(() => {
    return allBlogs.filter(blog => {
      const matchesSearch = !searchQuery || 
        blog.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        blog.meta_description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        blog.content?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesCategory = selectedCategory === 'all' || 
        (blog.category || 'Uncategorized') === selectedCategory;
      
      return matchesSearch && matchesCategory;
    });
  }, [allBlogs, searchQuery, selectedCategory]);

  // Get trending blogs (most recent, sorted by date)
  const trendingBlogs = useMemo(() => {
    return [...allBlogs]
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 5);
  }, [allBlogs]);

  // Get latest updates (most recent 3)
  const latestUpdates = useMemo(() => {
    return [...allBlogs]
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 3);
  }, [allBlogs]);

  // Pagination
  const totalPages = Math.ceil(filteredBlogs.length / blogsPerPage);
  const paginatedBlogs = useMemo(() => {
    const startIndex = (currentPage - 1) * blogsPerPage;
    return filteredBlogs.slice(startIndex, startIndex + blogsPerPage);
  }, [filteredBlogs, currentPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const siteUrl = typeof window !== 'undefined' ? window.location.origin : 'https://afriqcm.com';
  const currentUrl = typeof window !== 'undefined' ? window.location.href : `${siteUrl}/blogs`;

  return (
    <>
      <Helmet>
        <html lang="en" />
        <title>Blog | AFRIQ Bamenda - Academic Articles & Educational Resources</title>
        <meta name="description" content="Explore educational articles, academic resources, and insights from AFRIQ Bamenda. Stay updated with the latest news, tips, and guidance for competitive exam preparation." />
        <meta name="keywords" content="AFRIQ blog, educational articles, academic resources, exam preparation tips, Cameroon education, university preparation, concours preparation" />
        <link rel="canonical" href={currentUrl} />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content="Blog | AFRIQ Bamenda - Academic Articles & Educational Resources" />
        <meta property="og:description" content="Explore educational articles, academic resources, and insights from AFRIQ Bamenda." />
        <meta property="og:image" content={`${siteUrl}/logo512.png`} />
        <meta property="og:site_name" content="AFRIQ Bamenda" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={currentUrl} />
        <meta name="twitter:title" content="Blog | AFRIQ Bamenda" />
        <meta name="twitter:description" content="Explore educational articles, academic resources, and insights from AFRIQ Bamenda." />
        <meta name="twitter:image" content={`${siteUrl}/logo512.png`} />
        
        {/* Structured Data - CollectionPage Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": "AFRIQ Bamenda Blog",
            "description": "Educational articles, academic resources, and insights from AFRIQ Bamenda",
            "url": `${siteUrl}/blogs`,
            "publisher": {
              "@type": "Organization",
              "name": "AFRIQ Bamenda",
              "logo": {
                "@type": "ImageObject",
                "url": `${siteUrl}/logo512.png`
              }
            },
            "mainEntity": {
              "@type": "ItemList",
              "numberOfItems": allBlogs.length
            }
          })}
        </script>
        
        {/* Structured Data - Blog Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "AFRIQ Bamenda Blog",
            "description": "Educational articles, academic resources, and insights from AFRIQ Bamenda",
            "url": `${siteUrl}/blogs`,
            "publisher": {
              "@type": "Organization",
              "name": "AFRIQ Bamenda",
              "logo": {
                "@type": "ImageObject",
                "url": `${siteUrl}/logo512.png`
              }
            }
          })}
        </script>
      </Helmet>
      <TopNavbar />
      <HeroSection>
        <HeroContent>
          <AnimateOnScroll animation="fadeUp" delay={0} duration={800}>
            <HeroTitle>All Blog Articles</HeroTitle>
            <HeroSubtitle>
              Discover insights, tips, and updates from AFRIQ Bamenda
            </HeroSubtitle>
          </AnimateOnScroll>
        </HeroContent>
      </HeroSection>

      <MainContainer>
        <div className="container">
          {/* Search and Filter Section */}
          <AnimateOnScroll animation="fadeUp" delay={100} duration={800}>
            <SearchFilterSection>
              <SearchBox>
                <FaSearch className="search-icon" />
                <SearchInput
                  type="text"
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                />
                {searchQuery && (
                  <ClearButton onClick={() => setSearchQuery("")}>
                    <FaTimes />
                  </ClearButton>
                )}
              </SearchBox>
              <FilterBox>
                <FaFilter className="filter-icon" />
                <FilterSelect
                  value={selectedCategory}
                  onChange={(e) => {
                    setSelectedCategory(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>
                      {cat === 'all' ? 'All Categories' : cat}
                    </option>
                  ))}
                </FilterSelect>
              </FilterBox>
            </SearchFilterSection>
          </AnimateOnScroll>

          {/* Trending Section */}
          {trendingBlogs.length > 0 && (
            <AnimateOnScroll animation="fadeUp" delay={200} duration={800}>
              <Section>
                <SectionHeader>
                  <SectionTitle>
                    <FaFire className="section-icon" />
                    Trending Now
                  </SectionTitle>
                  <SectionSubtitle>Most popular and recent articles</SectionSubtitle>
                </SectionHeader>
                <TrendingGrid>
                  {trendingBlogs.map((blog, index) => (
                    <TrendingCard key={blog.id}>
                      <TrendingImage>
                        {blog.images && blog.images.length > 0 ? (
                          <img src={blog.images[0]} alt={blog.title} />
                        ) : (
                          <PlaceholderImage />
                        )}
                        <TrendingBadge>#{index + 1} Trending</TrendingBadge>
                      </TrendingImage>
                      <TrendingContent>
                        <TrendingCategory>{blog.category || 'Blog'}</TrendingCategory>
                        <TrendingTitle to={`/blog/${getSlugFromId(blog.id)}`}>
                          {blog.title}
                        </TrendingTitle>
                        <TrendingMeta>
                          <FaClock /> {formatDate(blog.created_at)}
                        </TrendingMeta>
                      </TrendingContent>
                    </TrendingCard>
                  ))}
                </TrendingGrid>
              </Section>
            </AnimateOnScroll>
          )}

          {/* Latest Updates Section */}
          {latestUpdates.length > 0 && (
            <AnimateOnScroll animation="fadeUp" delay={300} duration={800}>
              <Section>
                <SectionHeader>
                  <SectionTitle>
                    <FaClock className="section-icon" />
                    Latest Updates
                  </SectionTitle>
                  <SectionSubtitle>Stay up to date with our newest content</SectionSubtitle>
                </SectionHeader>
                <UpdatesGrid>
                  {latestUpdates.map((blog, index) => (
                    <UpdateCard key={blog.id}>
                      <UpdateNumber>{String(index + 1).padStart(2, '0')}</UpdateNumber>
                      <UpdateContent>
                        <UpdateTitle to={`/blog/${getSlugFromId(blog.id)}`}>
                          {blog.title}
                        </UpdateTitle>
                        <UpdateMeta>
                          <span>{formatDate(blog.created_at)}</span>
                          <span>•</span>
                          <span>{blog.category || 'Blog'}</span>
                        </UpdateMeta>
                      </UpdateContent>
                      <UpdateArrow to={`/blog/${getSlugFromId(blog.id)}`}>
                        <FaArrowRight />
                      </UpdateArrow>
                    </UpdateCard>
                  ))}
                </UpdatesGrid>
              </Section>
            </AnimateOnScroll>
          )}

          {/* All Blogs Grid */}
          <AnimateOnScroll animation="fadeUp" delay={400} duration={800}>
            <Section>
              <SectionHeader>
                <SectionTitle>All Articles</SectionTitle>
                <SectionSubtitle>
                  {filteredBlogs.length} {filteredBlogs.length === 1 ? 'article' : 'articles'} found
                </SectionSubtitle>
              </SectionHeader>

              {loading ? (
                <LoadingContainer>
                  <LoadingSpinner />
                  <p>Loading blog posts...</p>
                </LoadingContainer>
              ) : error ? (
                <ErrorContainer>
                  <p>Error: {error}</p>
                </ErrorContainer>
              ) : paginatedBlogs.length === 0 ? (
                <EmptyContainer>
                  <p>No articles found matching your criteria.</p>
                  <ClearFiltersButton onClick={() => {
                    setSearchQuery("");
                    setSelectedCategory("all");
                  }}>
                    Clear Filters
                  </ClearFiltersButton>
                </EmptyContainer>
              ) : (
                <>
                  <BlogsGrid>
                    {paginatedBlogs.map((blog, index) => (
                      <div key={blog.id} className="col-xs-12 col-sm-6 col-md-4 col-lg-4">
                        <AnimateOnScroll animation="fadeUp" delay={index * 50} duration={800}>
                          <BlogBox
                            img={blog.images && blog.images.length > 0 ? blog.images[0] : null}
                            title={blog.title}
                            text={blog.meta_description || blog.content?.replace(/<[^>]*>/g, '').substring(0, 150) + '...' || 'No description available'}
                            tag={blog.category || 'Blog'}
                            author={`${getAuthorName(blog.created_by)}, ${formatDate(blog.created_at)}`}
                            slug={getSlugFromId(blog.id)}
                          />
                        </AnimateOnScroll>
                      </div>
                    ))}
                  </BlogsGrid>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <Pagination>
                      <PaginationButton
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </PaginationButton>
                      <PageNumbers>
                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                          .filter(page => {
                            // Show first page, last page, current page, and pages around current
                            return page === 1 || 
                                   page === totalPages || 
                                   (page >= currentPage - 1 && page <= currentPage + 1);
                          })
                          .map((page, index, array) => {
                            // Add ellipsis if there's a gap
                            const prevPage = array[index - 1];
                            const showEllipsis = prevPage && page - prevPage > 1;
                            
                            return (
                              <React.Fragment key={page}>
                                {showEllipsis && <Ellipsis>...</Ellipsis>}
                                <PageNumber
                                  onClick={() => handlePageChange(page)}
                                  active={currentPage === page}
                                >
                                  {page}
                                </PageNumber>
                              </React.Fragment>
                            );
                          })}
                      </PageNumbers>
                      <PaginationButton
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </PaginationButton>
                    </Pagination>
                  )}
                </>
              )}
            </Section>
          </AnimateOnScroll>
        </div>
      </MainContainer>

      <Footer />
    </>
  );
}

const HeroSection = styled.section`
  width: 100%;
  padding: 120px 0 80px;
  background: linear-gradient(135deg, #4b2e83 0%, #6b46c1 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    opacity: 0.3;
  }

  @media (max-width: 768px) {
    padding: 100px 0 60px;
  }
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  text-align: center;
  position: relative;
  z-index: 1;
`;

const HeroTitle = styled.h1`
  font-size: clamp(2rem, 5vw, 3.5rem);
  font-weight: 900;
  color: #ffffff;
  margin-bottom: 20px;
  line-height: 1.2;
`;

const HeroSubtitle = styled.p`
  font-size: clamp(1rem, 2vw, 1.25rem);
  color: rgba(255, 255, 255, 0.9);
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const MainContainer = styled.main`
  width: 100%;
  padding: 60px 0;
  background: #f8f8ff;
  min-height: 60vh;
`;

const SearchFilterSection = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 50px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const SearchBox = styled.div`
  flex: 1;
  min-width: 250px;
  position: relative;
  display: flex;
  align-items: center;

  .search-icon {
    position: absolute;
    left: 20px;
    color: #4b2e83;
    font-size: 1.1rem;
    z-index: 1;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 16px 50px 16px 50px;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  font-size: 1rem;
  outline: none;
  transition: all 0.3s ease;
  background: #ffffff;

  &:focus {
    border-color: #4b2e83;
    box-shadow: 0 0 0 4px rgba(75, 46, 131, 0.1);
  }

  &::placeholder {
    color: #999;
  }
`;

const ClearButton = styled.button`
  position: absolute;
  right: 15px;
  background: transparent;
  border: none;
  color: #999;
  cursor: pointer;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.3s ease;

  &:hover {
    color: #4b2e83;
  }
`;

const FilterBox = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  min-width: 200px;

  .filter-icon {
    position: absolute;
    left: 20px;
    color: #4b2e83;
    font-size: 1.1rem;
    z-index: 1;
  }
`;

const FilterSelect = styled.select`
  width: 100%;
  padding: 16px 20px 16px 50px;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  font-size: 1rem;
  outline: none;
  transition: all 0.3s ease;
  background: #ffffff;
  cursor: pointer;
  appearance: none;

  &:focus {
    border-color: #4b2e83;
    box-shadow: 0 0 0 4px rgba(75, 46, 131, 0.1);
  }
`;

const Section = styled.section`
  margin-bottom: 80px;

  @media (max-width: 768px) {
    margin-bottom: 60px;
  }
`;

const SectionHeader = styled.div`
  margin-bottom: 40px;
  text-align: center;

  @media (max-width: 768px) {
    margin-bottom: 30px;
  }
`;

const SectionTitle = styled.h2`
  font-size: clamp(1.75rem, 4vw, 2.5rem);
  font-weight: 800;
  color: #4b2e83;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;

  .section-icon {
    color: #ff6b6b;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1rem;
  color: #666;
  max-width: 600px;
  margin: 0 auto;
`;

const TrendingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 30px;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const TrendingCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 40px rgba(75, 46, 131, 0.2);
  }
`;

const TrendingImage = styled.div`
  position: relative;
  width: 100%;
  height: 200px;
  overflow: hidden;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }

  ${TrendingCard}:hover & img {
    transform: scale(1.1);
  }
`;

const PlaceholderImage = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
`;

const TrendingBadge = styled.div`
  position: absolute;
  top: 15px;
  right: 15px;
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%);
  color: #ffffff;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 700;
  box-shadow: 0 4px 15px rgba(255, 107, 107, 0.4);
`;

const TrendingContent = styled.div`
  padding: 20px;
`;

const TrendingCategory = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  color: #4b2e83;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 10px;
`;

const TrendingTitle = styled(Link)`
  font-size: 1.1rem;
  font-weight: 700;
  color: #1a1a1a;
  text-decoration: none;
  display: block;
  margin-bottom: 12px;
  line-height: 1.4;
  transition: color 0.3s ease;

  &:hover {
    color: #4b2e83;
  }
`;

const TrendingMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  color: #666;

  svg {
    font-size: 0.75rem;
  }
`;

const UpdatesGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 40px;
`;

const UpdateCard = styled.div`
  background: #ffffff;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: translateX(8px);
    box-shadow: 0 4px 20px rgba(75, 46, 131, 0.15);
  }

  @media (max-width: 768px) {
    padding: 15px;
    gap: 15px;
  }
`;

const UpdateNumber = styled.div`
  font-size: 2rem;
  font-weight: 900;
  color: #4b2e83;
  opacity: 0.3;
  min-width: 50px;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 1.5rem;
    min-width: 40px;
  }
`;

const UpdateContent = styled.div`
  flex: 1;
`;

const UpdateTitle = styled(Link)`
  font-size: 1.1rem;
  font-weight: 700;
  color: #1a1a1a;
  text-decoration: none;
  display: block;
  margin-bottom: 8px;
  transition: color 0.3s ease;

  &:hover {
    color: #4b2e83;
  }

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const UpdateMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.875rem;
  color: #666;
  flex-wrap: wrap;
`;

const UpdateArrow = styled(Link)`
  color: #4b2e83;
  font-size: 1.2rem;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #f8f8ff;

  &:hover {
    background: #4b2e83;
    color: #ffffff;
    transform: translateX(4px);
  }
`;

const BlogsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: -15px;
  margin-bottom: 40px;

  > div {
    padding: 15px;
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-top: 50px;
  flex-wrap: wrap;
`;

const PaginationButton = styled.button`
  padding: 12px 24px;
  border: 2px solid #e0e0e0;
  background: #ffffff;
  color: #4b2e83;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    border-color: #4b2e83;
    background: #4b2e83;
    color: #ffffff;
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PageNumbers = styled.div`
  display: flex;
  gap: 5px;
  align-items: center;
`;

const PageNumber = styled.button`
  min-width: 40px;
  height: 40px;
  border: 2px solid ${props => props.active ? '#4b2e83' : '#e0e0e0'};
  background: ${props => props.active ? '#4b2e83' : '#ffffff'};
  color: ${props => props.active ? '#ffffff' : '#4b2e83'};
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: #4b2e83;
    background: #4b2e83;
    color: #ffffff;
  }
`;

const Ellipsis = styled.span`
  padding: 0 5px;
  color: #999;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;

  p {
    margin-top: 20px;
    color: #4b2e83;
    font-size: 1rem;
  }
`;

const LoadingSpinner = styled.div`
  width: 50px;
  height: 50px;
  border: 4px solid rgba(75, 46, 131, 0.1);
  border-top: 4px solid #4b2e83;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorContainer = styled.div`
  padding: 60px 20px;
  text-align: center;
  background: #fff5f5;
  border: 2px solid #ff6b6b;
  border-radius: 12px;
  margin: 40px 0;

  p {
    color: #ff6b6b;
    font-size: 1rem;
    font-weight: 600;
  }
`;

const EmptyContainer = styled.div`
  padding: 80px 20px;
  text-align: center;

  p {
    color: #666;
    font-size: 1.1rem;
    margin-bottom: 20px;
  }
`;

const ClearFiltersButton = styled.button`
  padding: 12px 24px;
  background: #4b2e83;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #6b46c1;
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(75, 46, 131, 0.3);
  }
`;

